// Missed calls (owner request, PJ, 2026-10-24: "when someone calls us back, it like pops us on our screen and rings. That's
// really great. I like that. But I also want a feature where, let's say if I was not at my computer or I happen to miss
// that call, I want somewhere I could see all my missed calls so I can know who called at what time so I can call them
// back."). The ringing pop-up stays exactly as it is; this is the list behind it: GET /api/inbound/missed-calls, its nav
// count and Mark done (src/worker/lib/missedCalls.ts, routes/inbound.ts), shown on web/src/pages/MissedCalls.tsx.
//
// The types, and the pure rules the Worker and the web share: which calls count as missed, what state each one is in
// (open / called back / done), how repeat calls fold into one row, and what "Call back" opens.
import type { DispositionCode, ISODate } from './api';

/** The page and the badge look back this many days. */
export const MISSED_CALL_DAYS = 30;
/** The same caller number within 24 hours of the row's newest call is one row ("called 3 times"). */
export const REPEAT_WINDOW_MS = 24 * 3600_000;
/** An inbound call nobody answered ends in one of these (twilio/webhooks.ts /inbound and /inbound-after; the cron fails a
 *  row stuck ringing for 2 h), always with answered_at NULL. An answered one is 'completed' with answered_at. */
export const MISSED_CALL_STATUSES = ['no_answer', 'busy', 'failed', 'canceled'] as const;
/** The nav badge refreshes this often while the app is open (PJ: "refreshed every 30-60 s"). */
export const MISSED_POLL_MS = 30_000;
/** Mark done's optional note. */
export const MISSED_NOTE_MAX = 500;

/** Twilio's stand-ins for a withheld caller ID (keypad spellings of ANONYMOUS, UNAVAILABLE, RESTRICTED). One list for
 *  the inbound webhooks (no call-back task for these) and this page (no Call back, never grouped). */
export const HIDDEN_CALLER_IDS: ReadonlySet<string> = new Set(['+266696687', '+86282452253', '+7378742833']);
const E164 = /^\+[1-9]\d{7,14}$/;

/** The number to call back, or null when the caller's number was hidden ('unknown', a stand-in, or not E.164). */
export function callableCaller(from: string | null | undefined): string | null {
  return from && E164.test(from) && !HIDDEN_CALLER_IDS.has(from) ? from : null;
}

export type MissedScope = 'mine' | 'all';
export type MissedStatus = 'open' | 'called_back' | 'done';

/** Someone on the team: first name; "Valistix" for an admin while admins' activity is hidden from the viewer (PJ
 *  2026-10-07, userId null then); isMe = the viewer ("You called them"). */
export interface MissedActor { userId: number | null; name: string | null; isMe: boolean }

/** How a missed call stopped being open: our call back to that number ('called_back'), a later call from that number we
 *  answered ('answered'), marked done (by hand, or its task done / voicemail handled: 'done'), or the number is on the
 *  do-not-call list / the business asked us to stop ('dnc', nothing stored, no time). */
export type SettledHow = 'called_back' | 'answered' | 'done' | 'dnc';
export interface MissedSettled { how: SettledHow; at: ISODate | null; by: MissedActor | null; note: string | null }

/** What the Worker read about one missed call. */
export interface MissedFacts {
  /** The first placed outbound call to the number they called from, after their call (any setter, lead or campaign). */
  calledBack: { at: ISODate; by: MissedActor | null } | null;
  /** The first later inbound call from that number that we answered. */
  answeredLater: { at: ISODate; by: MissedActor | null } | null;
  /** calls.handled_at / handled_by / handled_note (migration 0031). */
  done: { at: ISODate; by: MissedActor | null; note: string | null } | null;
  /** The number is on our do-not-call list, or the business it matched is do-not-contact. */
  dnc: boolean;
}

/** The earliest of the call back, the answered later call and done (so a "done" written after a call back reads as
 *  called back); else 'dnc' for a number we must not call; else null (open). Pure. */
export function missedSettled(f: MissedFacts): MissedSettled | null {
  const events: MissedSettled[] = [];
  if (f.calledBack) events.push({ how: 'called_back', at: f.calledBack.at, by: f.calledBack.by, note: null });
  if (f.answeredLater) events.push({ how: 'answered', at: f.answeredLater.at, by: f.answeredLater.by, note: null });
  if (f.done) events.push({ how: 'done', at: f.done.at, by: f.done.by, note: f.done.note });
  if (events.length) {
    let best = events[0];
    for (const e of events.slice(1)) if (Date.parse(e.at as string) < Date.parse(best.at as string)) best = e;
    return best;
  }
  return f.dnc ? { how: 'dnc', at: null, by: null, note: null } : null;
}

export function missedStatusOf(s: MissedSettled | null): MissedStatus {
  if (!s) return 'open';
  return s.how === 'called_back' || s.how === 'answered' ? 'called_back' : 'done';
}

/**
 * Repeat calls folded into rows (PJ 2026-10-24: "the same caller number within 24 hours is one row"): newest first, a call
 * joins the row of the same callable number while it is within REPEAT_WINDOW_MS of that row's newest call; an older one
 * starts a new row. A hidden number is never folded (nothing says two hidden calls are one caller). Pure; the input order
 * doesn't matter (sorted newest first here, ties by id).
 */
export function groupRepeats<T extends { callId: number; fromE164: string | null; at: ISODate }>(calls: readonly T[]): T[][] {
  const sorted = [...calls].sort((a, b) => Date.parse(b.at) - Date.parse(a.at) || b.callId - a.callId);
  const rows: T[][] = [];
  const current = new Map<string, T[]>();
  for (const c of sorted) {
    const from = callableCaller(c.fromE164);
    const row = from ? current.get(from) : undefined;
    if (row && Date.parse(row[0].at) - Date.parse(c.at) <= REPEAT_WINDOW_MS) {
      row.push(c);
      continue;
    }
    const fresh = [c];
    rows.push(fresh);
    if (from) current.set(from, fresh);
  }
  return rows;
}

/** A row is open while any of its calls is open; else it reads as its newest call. `statuses` newest first. */
export function rowStatus(statuses: readonly MissedStatus[]): MissedStatus {
  return statuses.includes('open') ? 'open' : statuses[0] ?? 'done';
}

/** Which line of the business the caller's number is (src/shared/api-numbers.ts leadNumbers): the owner's line, the
 *  store line, the business's one number ('main'), or none of its numbers any more ('other'). */
export type MatchLine = 'owner' | 'store' | 'main' | 'other';
export interface MissedCaller { businessId: number; name: string; city: string | null; stateProv: string | null; line: MatchLine }

/**
 * What "Call back" opens (PJ 2026-10-24: it opens that lead in the dialer and dials the number they called from):
 *   'lead'     the lead row in a live campaign the viewer works (the one the call rang for first), dialled through the
 *              normal gated prepare on the number they called from (owner's line or store line);
 *   'business' the business with no lead row the viewer can open (a no-campaign / pad number, or every lead row of it is
 *              closed or in a paused campaign): the dial pad's no-campaign item, same gate;
 *   'pad'      an unknown number, or one several businesses hold: the dial pad's own no-campaign flow with the number
 *              filled in (it finds a lead holding the number, or makes "Unknown business"; the wrap-up asks to save it).
 */
export type MissedCallBack =
  | { how: 'lead'; membershipId: number; businessId: number }
  | { how: 'business'; businessId: number }
  | { how: 'pad' };

/** One lead row of the business, as pickCallBack weighs it. */
export interface CallBackLeadOption {
  membershipId: number;
  ownerUserId: number | null;
  campaignLive: boolean;
  /** Not closed: closed_lost, disqualified, bad_data and dnc are (an exhausted lead asks "Call again?", and since PJ
   *  2026-10-28 a meeting booked asks "Meeting booked. Call anyway?"). */
  open: boolean;
  /** The viewer is assigned to its campaign (admins may open any). */
  assigned: boolean;
}

// PJ 2026-10-28: 'meeting' is not here: Call back opens a booked lead as the lead, so the dialer shows "Meeting booked"
// and the call goes on it.
export const CLOSED_LEAD_STATUSES: readonly string[] = ['closed_lost', 'disqualified', 'bad_data', 'dnc'];

/** The Call back plan for a row (its newest call), or null with why in plain words. Pure. */
export function pickCallBack(args: {
  fromE164: string | null;
  dnc: boolean;
  /** The business the call matched (or the one business holding the number); null = unknown or several. */
  businessId: number | null;
  /** The lead row the inbound call was filed under (our last call to them was from it). */
  callMembershipId: number | null;
  viewer: { id: number; admin: boolean };
  leads: readonly CallBackLeadOption[];
  /** The viewer may open the business with no lead row (admins; a setter whose campaign holds it, or whose pad number it is). */
  canOpenBusiness: boolean;
}): { plan: MissedCallBack | null; why: string | null } {
  if (!callableCaller(args.fromE164)) return { plan: null, why: 'Their number was hidden, so there is no number to call back.' };
  if (args.dnc) return { plan: null, why: "They're on the do-not-call list. Never call them back." };
  if (args.businessId === null) return { plan: { how: 'pad' }, why: null };
  const usable = args.leads.filter((l) => l.campaignLive && l.open && (args.viewer.admin || l.assigned));
  usable.sort((a, b) =>
    Number(b.membershipId === args.callMembershipId) - Number(a.membershipId === args.callMembershipId)
    || Number(b.ownerUserId === args.viewer.id) - Number(a.ownerUserId === args.viewer.id)
    || b.membershipId - a.membershipId);
  if (usable[0]) return { plan: { how: 'lead', membershipId: usable[0].membershipId, businessId: args.businessId }, why: null };
  if (args.canOpenBusiness) return { plan: { how: 'business', businessId: args.businessId }, why: null };
  return { plan: { how: 'pad' }, why: null };
}

export interface MissedVoicemail { id: number; durationSec: number | null; createdAt: ISODate; handledAt: ISODate | null; audioUrl: string }

/** One time they called (a row lists every time on expand). */
export interface MissedCallTime {
  callId: number;
  at: ISODate;
  status: MissedStatus;
  settled: MissedSettled | null;
  /** Who it rang; null = no one in particular (every admin). */
  rang: MissedActor | null;
  voicemails: MissedVoicemail[];
}

/** "Our last call to them before the missed call": when, by whom, the outcome logged (never its notes). */
export interface MissedLastCall { at: ISODate; by: MissedActor | null; outcome: DispositionCode | null }

export interface MissedCallRow {
  /** The newest call of the row (Mark done and the row key use it). */
  callId: number;
  /** The number they called from; null = hidden. */
  fromE164: string | null;
  /** Every time, newest first; times.length > 1 = "called 3 times". */
  times: MissedCallTime[];
  status: MissedStatus;
  /** The newest call's settlement when the row is not open. */
  settled: MissedSettled | null;
  /** The business the call matched; null = unknown number, or several (see candidates). */
  caller: MissedCaller | null;
  /** Several businesses hold this number (a multi-store owner's line) and our last call settles none of them: names and
   *  cities only. Empty otherwise. */
  candidates: MissedCaller[];
  /** The prospect's zone (the business's; else the area code's); null when unknown. */
  theirTz: string | null;
  /** Which of our numbers they called. */
  ourNumber: { e164: string; metro: string | null } | null;
  /** Our last placed call to that number before their newest call. */
  lastCall: MissedLastCall | null;
  dnc: boolean;
  callBack: MissedCallBack | null;
  /** Why there is no Call back (hidden number, do-not-call). */
  callBackWhy: string | null;
  /** The viewer may mark one of the row's open calls done (review 2026-10-24: it rang him, or he is an admin). */
  canMarkDone: boolean;
  /** Why an Open row has no Mark done for the viewer (who it rang: markDoneWhy); null otherwise. */
  markDoneWhy: string | null;
}

/** Why a setter can't mark an open missed call done (review 2026-10-24: the voicemail box's and the Tasks page's rule,
 *  the one it rang or an admin), from who it rang as the viewer sees them: null = the admins (it rang every admin), a
 *  hidden admin (userId null, "Valistix") = an admin. Pure. */
export function markDoneWhy(rang: MissedActor | null): string {
  if (!rang) return 'It rang the admins, so only an admin can mark it done.';
  if (rang.userId === null) return 'It rang an admin, so only an admin can mark it done.';
  if (!rang.name) return 'It rang someone else, so only they or an admin can mark it done.';
  return `It rang ${rang.name}, so only ${rang.name} or an admin can mark it done.`;
}

export interface MissedCallList { scope: MissedScope; days: number; open: number; rows: MissedCallRow[] }
/** The nav badge: my open missed calls (rows, as the page shows them). */
export interface MissedCallCount { open: number }
export interface MissedDoneRequest { note?: string | null }
export interface MissedDoneResponse { callIds: number[]; tasksClosed: number; voicemailsHandled: number }
